/**
 * جدولِ تفکیکِ آدرس در سربرگِ «آدرس‌ها» (WHOSUP-277).
 *
 * هر ردیف یک ساختمان است، با اجزایی که کرالر از آدرس درآورده (WHOSUP-274) و
 * در گزارشِ `address_table` به app فرستاده (WHOSUP-276). مایگریشنِ ۰۱۵۷ فقط
 * **واقعیت** می‌دهد — همان گزارش + شمارِ رویداد و فلگ‌های زنده؛ این فایل
 * تصمیم می‌گیرد چه چیزی مشکوک است، چه چیزی چطور مرتب و فیلتر می‌شود و هر
 * خانه از کجا آمده. همان تقسیمی که `addressReport.ts` و `aliasReport.ts`
 * دارند، تا با `npm test` آزمودنی بماند.
 *
 * ═══ قرمز یعنی «نگاه کن»، نه «غلط است» ═══
 *
 * چهار قاعده، همه برای پیدا کردنِ دو ردیفی که شاید یک ساختمان‌اند یا یکی‌شان
 * جزءِ غلطی گرفته:
 *
 *   • شهرِ رویداد + خیابان + پلاکِ یکسان
 *   • پلاکِ یکسان در همان شهر (به‌تنهایی)
 *   • کدپستیِ یکسان
 *   • مختصات کمتر از ۲۰۰ متر با ردیفِ دیگر — برابریِ دقیق تقریباً هیچ‌وقت
 *     رخ نمی‌دهد، پس فاصله ملاک است.
 *
 * ⚠ خانه‌ی خالی هیچ‌وقت با خانه‌ی خالیِ دیگر تکراری نیست؛ وگرنه نیمی از
 * جدول (همه‌ی بی‌کدپستی‌ها) قرمز می‌شد و قرمز بی‌معنی.
 */

/** منشأِ یک جزء، همان سه مقداری که کرالر در `part_origins` می‌نویسد. */
export type Origin = 'source' | 'rule' | 'model';

export const ORIGIN_LABEL: Record<Origin, string> = {
  source: 'منبع', rule: 'قاعده', model: 'مدل',
};

export const ORIGIN_HELP: Record<Origin, string> = {
  source: 'خودِ منبع این تکه را جدا داده بود (مثلاً postalCode در JSON-LD).',
  rule: 'از متنِ آدرس با قاعده درآمده (واژگانِ registry/lexicon.yaml).',
  model: 'یک مدلِ زبانی حدس زده.',
};

/** ردیفِ خام، همان‌طور که `admin_address_table()` پس می‌دهد. */
export interface AddressTableRowRaw {
  address: string;
  address_hash: string;
  addresses?: string[];
  address_hashes?: string[];
  key_kind?: string | null;
  key_value?: string | null;
  source_cities?: string[] | null;
  event_city?: string | null;
  country?: string | null;
  region?: string | null;
  district?: string | null;
  street?: string | null;
  street_type?: string | null;
  number?: string | null;
  postal_code?: string | null;
  venues?: string | null;
  lat?: number | null;
  lng?: number | null;
  part_origins?: Record<string, string> | null;
  alternatives?: Record<string, string[]> | null;
  live_events?: number;
  past_events?: number;
  sample?: { id: string; title: string | null; starts_at: string | null; url: string | null } | null;
  flagged_columns?: string[] | null;
  flag_note?: string | null;
  flagged_at?: string | null;
}

export interface AddressTableData {
  state: 'missing' | 'not-precomputed' | 'ready';
  computed_at?: string | null;
  flags_table?: boolean;
  columns?: string[];
  rows?: AddressTableRowRaw[];
}

/** ستون‌هایی که در جدول هستند، به ترتیبِ تیکت. */
export type ColKey =
  | 'source_cities' | 'event_city' | 'country' | 'region' | 'district' | 'street'
  | 'number' | 'venues' | 'postal_code' | 'coordinates' | 'flags'
  | 'live_events' | 'past_events' | 'sample';

/** ستون‌هایی که ادمین می‌تواند «غلط» علامت بزند — همان فهرستِ ۰۱۵۷. */
export const FLAGGABLE = [
  'source_cities', 'event_city', 'country', 'region', 'district', 'street',
  'number', 'venues', 'postal_code', 'coordinates',
] as const;
export type FlagCol = typeof FLAGGABLE[number];

export interface ColSpec {
  key: ColKey;
  label: string;
  help: string;
  numeric?: boolean;
}

export const COLUMNS: ColSpec[] = [
  { key: 'source_cities', label: 'شهرِ منبع', help: 'شهرِ منبع‌هایی که این آدرس را آورده‌اند. اگر از منبع‌های چند شهر آمده، همه با کاما.' },
  { key: 'event_city', label: 'شهرِ رویداد', help: 'شهری که رویداد در آن برگزار می‌شود.' },
  { key: 'country', label: 'کشور', help: 'کدِ دوحرفیِ کشور.' },
  { key: 'region', label: 'استان یا ایالت', help: 'کدِ استان یا ایالت.' },
  { key: 'district', label: 'ناحیه یا محله', help: 'اغلب خالی است.' },
  { key: 'street', label: 'خیابان', help: 'بی‌کلمه‌ی نوع: Bank، نه Bank Street یا Bank St. نوع جدا نگه داشته می‌شود و در راهنمای خانه دیده می‌شود.' },
  { key: 'number', label: 'پلاک', help: 'شماره‌ی ساختمان.' },
  { key: 'venues', label: 'تمامی سالن‌ها', help: 'همه‌ی سالن‌هایی که در این آدرس دیده شده‌اند، با کاما.' },
  { key: 'postal_code', label: 'کدپستی', help: 'کدپستی.' },
  { key: 'coordinates', label: 'مختصات', help: 'عرض و طولِ جغرافیایی.' },
  { key: 'flags', label: 'فلگ', help: 'ستون‌هایی از این ردیف که غلط‌اند. در crawler.address_flags ذخیره می‌شود و کرالر شبانه برش می‌دارد.' },
  { key: 'live_events', label: 'رویدادِ زنده', help: 'رویدادهای منتشرشده‌ای که هنوز تمام نشده‌اند و آدرسشان یکی از نگارش‌های همین ساختمان است. تطبیق با متنِ آدرس است، پس تقریبی.', numeric: true },
  { key: 'past_events', label: 'رویدادِ گذشته', help: 'رویدادهایی با همین آدرس که زمانشان گذشته. تطبیق با متنِ آدرس است، پس تقریبی.', numeric: true },
  { key: 'sample', label: 'رویدادِ نمونه', help: 'نزدیک‌ترین رویدادِ زنده، وگرنه تازه‌ترینِ گذشته.' },
];

export const COLUMN_LABEL = Object.fromEntries(COLUMNS.map((c) => [c.key, c.label])) as Record<ColKey, string>;

/** مرزِ «نزدیک» برای مختصات، به متر. */
export const NEAR_METERS = 200;

export interface AddressRow {
  /** کلیدِ پایدار: شهر + hashِ آدرسِ اصلی — همان کلیدِ `crawler.address_flags`. */
  id: string;
  city: string;
  address: string;
  addresses: string[];
  /** متنِ نمایشیِ هر ستون؛ رشته‌ی خالی یعنی خالی. */
  cells: Record<ColKey, string>;
  lat: number | null;
  lng: number | null;
  live: number;
  past: number;
  sample: AddressTableRowRaw['sample'];
  streetType: string | null;
  flags: FlagCol[];
  flagNote: string | null;
  flaggedAt: string | null;
  origins: Partial<Record<ColKey, Origin>>;
  alternatives: Partial<Record<ColKey, string[]>>;
  /** چرا هر خانه قرمز است؛ ستونی که این‌جا نیست قرمز نیست. */
  suspects: Partial<Record<ColKey, string[]>>;
}

const clean = (v: unknown): string =>
  typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '';

const isOrigin = (v: unknown): v is Origin => v === 'source' || v === 'rule' || v === 'model';

export function formatCoord(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

/** فاصله‌ی دو نقطه به متر (هاورساین). */
export function distanceMeters(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (bLat - aLat) * rad;
  const dLng = (bLng - aLng) * rad;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function toRow(r: AddressTableRowRaw): AddressRow {
  const lat = typeof r.lat === 'number' && Number.isFinite(r.lat) ? r.lat : null;
  const lng = typeof r.lng === 'number' && Number.isFinite(r.lng) ? r.lng : null;
  const city = clean(r.event_city);
  const origins: Partial<Record<ColKey, Origin>> = {};
  for (const [k, v] of Object.entries(r.part_origins ?? {})) {
    if (isOrigin(v) && COLUMNS.some((c) => c.key === k)) origins[k as ColKey] = v;
  }
  const alternatives: Partial<Record<ColKey, string[]>> = {};
  for (const [k, v] of Object.entries(r.alternatives ?? {})) {
    if (Array.isArray(v) && v.length > 0 && COLUMNS.some((c) => c.key === k)) {
      alternatives[k as ColKey] = v.map(String);
    }
  }
  const flags = (r.flagged_columns ?? [])
    .filter((c): c is FlagCol => (FLAGGABLE as readonly string[]).includes(c));
  return {
    id: `${city}|${r.address_hash}`,
    city,
    address: r.address,
    addresses: r.addresses?.length ? r.addresses : [r.address],
    cells: {
      source_cities: (r.source_cities ?? []).map(clean).filter(Boolean).join(', '),
      event_city: city,
      country: clean(r.country),
      region: clean(r.region),
      district: clean(r.district),
      street: clean(r.street),
      number: clean(r.number),
      venues: clean(r.venues),
      postal_code: clean(r.postal_code),
      coordinates: lat != null && lng != null ? formatCoord(lat, lng) : '',
      flags: flags.map((f) => COLUMN_LABEL[f]).join(', '),
      live_events: String(r.live_events ?? 0),
      past_events: String(r.past_events ?? 0),
      sample: clean(r.sample?.title),
    },
    lat, lng,
    live: r.live_events ?? 0,
    past: r.past_events ?? 0,
    sample: r.sample ?? null,
    streetType: clean(r.street_type) || null,
    flags,
    flagNote: r.flag_note ?? null,
    flaggedAt: r.flagged_at ?? null,
    origins,
    alternatives,
    suspects: {},
  };
}

const norm = (s: string) => s.toLowerCase();
const normPostal = (s: string) => s.toUpperCase().replace(/[\s-]/g, '');

function mark(row: AddressRow, col: ColKey, why: string) {
  (row.suspects[col] ??= []).push(why);
}

/** ردیف‌ها را با کلیدِ داده‌شده گروه می‌کند؛ کلیدِ `null` (خانه‌ی خالی) بیرون می‌ماند. */
function dupes(rows: AddressRow[], key: (r: AddressRow) => string | null): AddressRow[][] {
  const groups = new Map<string, AddressRow[]>();
  for (const r of rows) {
    const k = key(r);
    if (k == null) continue;
    const g = groups.get(k);
    if (g) g.push(r); else groups.set(k, [r]);
  }
  return [...groups.values()].filter((g) => g.length > 1);
}

/** چهار قاعده‌ی قرمز را روی ردیف‌ها می‌نشاند (درجا). */
export function markSuspects(rows: AddressRow[]): void {
  for (const g of dupes(rows, (r) => (r.city && r.cells.street && r.cells.number
    ? `${norm(r.city)}|${norm(r.cells.street)}|${norm(r.cells.number)}` : null))) {
    const why = `همان شهر و خیابان و پلاک با ${g.length - 1} ردیفِ دیگر`;
    for (const r of g) { mark(r, 'street', why); mark(r, 'number', why); }
  }
  for (const g of dupes(rows, (r) => (r.city && r.cells.number
    ? `${norm(r.city)}|${norm(r.cells.number)}` : null))) {
    const why = `همین پلاک در همین شهر در ${g.length - 1} ردیفِ دیگر`;
    for (const r of g) mark(r, 'number', why);
  }
  for (const g of dupes(rows, (r) => (r.cells.postal_code ? normPostal(r.cells.postal_code) : null))) {
    const why = `همین کدپستی در ${g.length - 1} ردیفِ دیگر`;
    for (const r of g) mark(r, 'postal_code', why);
  }
  // مختصات: مرتب بر حسبِ عرض و فقط همسایه‌هایی که از نظرِ عرض نزدیک‌اند —
  // وگرنه چند هزار ردیف یعنی میلیون‌ها مقایسه.
  const pts = rows.filter((r) => r.lat != null && r.lng != null)
    .sort((a, b) => (a.lat as number) - (b.lat as number));
  const latWindow = NEAR_METERS / 111_000;
  const near = new Map<AddressRow, number[]>();
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    for (let j = i + 1; j < pts.length; j++) {
      const b = pts[j];
      if ((b.lat as number) - (a.lat as number) > latWindow) break;
      const d = distanceMeters(a.lat as number, a.lng as number, b.lat as number, b.lng as number);
      if (d < NEAR_METERS) {
        (near.get(a) ?? near.set(a, []).get(a)!).push(d);
        (near.get(b) ?? near.set(b, []).get(b)!).push(d);
      }
    }
  }
  for (const [r, ds] of near) {
    mark(r, 'coordinates',
      `${ds.length} ردیفِ دیگر کمتر از ${NEAR_METERS} متر دورند (نزدیک‌ترین ${Math.round(Math.min(...ds))} متر)`);
  }
}

export interface AddressTable {
  state: AddressTableData['state'];
  computedAt: string | null;
  flagsTable: boolean;
  rows: AddressRow[];
  /** ردیف‌هایی که دست‌کم یک خانه‌ی قرمز دارند. */
  suspectRows: number;
  flaggedRows: number;
}

export function buildAddressTable(data: AddressTableData): AddressTable {
  const rows = data.state === 'ready' ? (data.rows ?? []).map(toRow) : [];
  markSuspects(rows);
  return {
    state: data.state,
    computedAt: data.computed_at ?? null,
    flagsTable: data.flags_table ?? false,
    rows,
    suspectRows: rows.filter((r) => Object.keys(r.suspects).length > 0).length,
    flaggedRows: rows.filter((r) => r.flags.length > 0).length,
  };
}

/** متنِ راهنمای یک خانه: از کجا آمده، مقدارِ رقیب، و چرا قرمز است. */
export function cellProvenance(row: AddressRow, col: ColKey): string {
  const lines: string[] = [];
  const o = row.origins[col];
  if (o) lines.push(`از کجا: ${ORIGIN_LABEL[o]} — ${ORIGIN_HELP[o]}`);
  else if (col === 'source_cities' || col === 'event_city') lines.push('از کجا: منبع — شهری که منبع برایش ثبت شده.');
  else if (col === 'venues') lines.push('از کجا: منبع — نامِ سالن همان‌طور که منبع‌ها نوشته‌اند.');
  else if (col === 'live_events' || col === 'past_events' || col === 'sample') lines.push('از کجا: شمارشِ app روی جدولِ رویدادها.');
  else if (col !== 'flags' && row.cells[col]) lines.push('از کجا: نامعلوم — کرالر منشأ را نفرستاده.');
  if (col === 'street' && row.streetType) lines.push(`نوعِ خیابان: ${row.streetType}`);
  const alt = row.alternatives[col];
  if (alt?.length) lines.push(`مقدارِ دیگرِ منبع‌ها: ${alt.join(' · ')}`);
  for (const why of row.suspects[col] ?? []) lines.push(`مشکوک: ${why}`);
  if (col === 'flags' && row.flagNote) lines.push(`یادداشت: ${row.flagNote}`);
  return lines.join('\n');
}

export interface SortState { col: ColKey; dir: 'asc' | 'desc' }

function sortValue(r: AddressRow, col: ColKey): number | string | null {
  if (col === 'live_events') return r.live;
  if (col === 'past_events') return r.past;
  if (col === 'number') {
    const n = parseInt(r.cells.number, 10);
    return Number.isFinite(n) ? n : (r.cells.number || null);
  }
  if (col === 'coordinates') return r.lat;
  return r.cells[col] || null;
}

/** مرتب‌سازی؛ خانه‌ی خالی همیشه ته، چه صعودی چه نزولی. */
export function sortRows(rows: AddressRow[], s: SortState): AddressRow[] {
  const k = s.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = sortValue(a, s.col);
    const vb = sortValue(b, s.col);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * k;
    if (typeof va === 'number') return -1 * k;
    if (typeof vb === 'number') return 1 * k;
    return va.localeCompare(vb) * k;
  });
}

export interface TableFilter {
  /** فیلترِ متنیِ هر ستون. `>3`، `<1`، `=0` روی ستون‌های عددی مقایسه است. */
  cols: Partial<Record<ColKey, string>>;
  onlySuspect: boolean;
}

/** `"-"` یعنی «فقط خالی‌ها» — بدونش راهی برای پیدا کردنِ بی‌کدپستی‌ها نبود. */
export function matchCell(value: string, filter: string, numeric = false): boolean {
  const f = filter.trim();
  if (!f) return true;
  if (f === '-') return value === '';
  const m = numeric ? /^([<>=])\s*(\d+)$/.exec(f) : null;
  if (m) {
    const v = Number(value);
    const n = Number(m[2]);
    return m[1] === '>' ? v > n : m[1] === '<' ? v < n : v === n;
  }
  return value.toLowerCase().includes(f.toLowerCase());
}

export function filterRows(rows: AddressRow[], f: TableFilter): AddressRow[] {
  const active = COLUMNS.filter((c) => (f.cols[c.key] ?? '').trim() !== '');
  return rows.filter((r) => {
    if (f.onlySuspect && Object.keys(r.suspects).length === 0) return false;
    return active.every((c) => matchCell(r.cells[c.key], f.cols[c.key] as string, c.numeric));
  });
}
